import React, { useState } from 'react';
import { Title, Drawer, Body, Button } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { useUrl } from 'hooks/useUrls';
import {
  DigitalTwin,
  useAddSchemaCollectionToDigitalTwinMutation,
  useGetDigitalTwinQuery,
} from 'types/dt-graphql-apollo-v2';
import mixpanel from 'utils/mixpanel-frontend';
import { notification } from 'antd';
import { v1 as uuidv1 } from 'uuid';
import { ProjectListItem } from 'hooks/projects';
import { createRefId } from 'utils/misc';
import { SelectedProjectList, SolutionCardsList } from './SolutionCardsList';

export const AddSolutionsDrawer = ({
  digitalTwin,
  onCancel,
  onSetShowAddSolutionsDrawer,
  visible,
}: {
  digitalTwin: DigitalTwin;
  onCancel: VoidFunction;
  onSetShowAddSolutionsDrawer: (showState: boolean) => void;
  visible: boolean;
}) => {
  const [selectedProjectList, setSelectedProjectList] =
    useState<SelectedProjectList>({});
  const selectedProjectEntries = Object.entries(selectedProjectList);
  const [isAdding, setIsAdding] = useState(false);
  const [addSchemaCollection] = useAddSchemaCollectionToDigitalTwinMutation();
  const tenant = useUrl.Tenant();

  const { refetch: refetchDigitalTwin } = useGetDigitalTwinQuery({
    variables: {
      id: digitalTwin.externalId,
    },
  });

  const handleProjectListChange = (
    project: ProjectListItem,
    selected: boolean,
    version?: string
  ) => {
    if (selected) {
      // add project to selectedProjectList
      const newList = {
        ...selectedProjectList,
        [project.id]: {
          name: project.name,
          tenant: project.tenant,
          version,
          space: project.space,
        },
      };
      setSelectedProjectList(newList);
    } else {
      const list = { ...selectedProjectList };
      delete list[project.id];
      setSelectedProjectList(list);
    }
  };

  const onAddToProject = async () => {
    if (isAdding) return;

    setIsAdding(true);
    try {
      const addSchemaCollectionRequests = selectedProjectEntries.map(
        ([dataModel, project]) =>
          addSchemaCollection({
            variables: {
              refId: createRefId(project.space, dataModel, project.version),
              name: project.name,
              schemaCollectionId: uuidv1(),
              tenant: project.tenant,
              digitalTwinId: digitalTwin.externalId,
            },
          })
      );
      await Promise.all(addSchemaCollectionRequests);

      selectedProjectEntries.forEach(([id, selectedProject]) => {
        notification.success({
          message: `${selectedProject.name} was added`,
          description: `Successfully added ${selectedProject.name}`,
        });
        mixpanel.track('Add schema to project', {
          id,
          name: selectedProject.name,
        });
      });
      onSetShowAddSolutionsDrawer(false);
      setSelectedProjectList({});
      await refetchDigitalTwin();
    } catch (err: any) {
      notification.error({
        message: `Could not add ${selectedProjectList?.[0]?.name}`,
        description: err.toString(),
      });
    } finally {
      setIsAdding(false);
    }
  };

  const renderButtons = () => (
    <StyledFooter>
      <StyledButtons>
        <Button
          disabled={isAdding}
          onClick={() => onSetShowAddSolutionsDrawer(false)}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={onAddToProject}
          loading={isAdding}
          disabled={isAdding || selectedProjectEntries.length === 0}
        >
          Add to project
        </Button>
      </StyledButtons>
    </StyledFooter>
  );

  const existingSolutionIds =
    digitalTwin.schemaCollections?.items?.map((sc) => sc!.refId ?? '') || [];

  return (
    <StyledDrawer
      onCancel={() => {
        onCancel();
      }}
      width="90%"
      visible={visible}
      footer={renderButtons()}
    >
      <StyledTitle level={3}>Add Schemas</StyledTitle>
      <StyledBody level={1}>
        {`Select among available schemas within the tenant
        ${tenant}. The schemas are managed in ITG. If you are missing a
        schema in the list, you may need access to view it.`}
      </StyledBody>
      <SolutionCardsList
        existingSolutionIds={existingSolutionIds}
        selectedProjectList={selectedProjectList}
        onHandleProjectChange={handleProjectListChange}
      />
    </StyledDrawer>
  );
};

const StyledDrawer = styled(Drawer)`
  .drawer-content-wrapper {
    max-width: 886px;
  }
  .cogs-drawer-content {
    padding: 3rem;
  }
  .cogs-drawer-close {
    cursor: pointer;
  }
`;

const StyledTitle = styled(Title)`
  margin-bottom: 1rem;
`;

const StyledBody = styled(Body)`
  margin-bottom: 1rem;
`;

const StyledButtons = styled.div`
  display: flex;
  margin-top: 2rem;
  justify-content: flex-end;
  gap: 1rem;
`;

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
`;
